<template>
  <div class="my-teaching-log">
    <!-- 搜索栏 -->
    <div class="search-bar">
      <StudentPicker v-model="selectedStudent" @change="onStudentChange" />
    </div>

    <!-- 统计卡片 -->
    <div class="stats-card">
      <div class="stat-item" @click="goToComposition(1)">
        <div class="stat-value">{{ classHourStatics.totalClassHourCount || 0 }}</div>
        <div class="stat-label">累计课时</div>
      </div>
      <div class="stat-divider"></div>
      <div class="stat-item" @click="goToComposition(2)">
        <div class="stat-value">{{ classHourStatics.monthClassHourCount || 0 }}</div>
        <div class="stat-label">本月课时</div>
      </div>
      <div class="stat-divider"></div>
      <div class="stat-item" @click="goToComposition(3)">
        <div class="stat-value">{{ classHourStatics.weekClassHourCount || 0 }}</div>
        <div class="stat-label">本周课时</div>
      </div>
    </div>

    <!-- 课程列表 -->
    <div class="log-list" ref="listRef" @scroll="handleScroll">
      <div v-for="(dayLogs, date) in groupedTeachingLogs" :key="date" class="date-section">
        <div class="date-header">
          <span class="date-text">{{ date }}</span>
          <span class="week-text">{{ dayLogs[0].week }}</span>
          <span class="count-badge">{{ dayLogs.length }}节课</span>
        </div>
        <div class="card-list">
          <div v-for="(item, idx) in dayLogs" :key="idx" class="course-card" @click="goToDetail(item.schedulePlanId)">
            <div class="card-left">
              <div class="time-badge">
                <span class="time">{{ item.startTime }}</span>
                <span class="time-divider">-</span>
                <span class="time">{{ item.endTime }}</span>
              </div>
            </div>
            <div class="card-content">
              <div class="course-name">{{ item.courseName }}</div>
              <div class="student-row">
                <van-icon name="friends-o" size="12" />
                <span class="student-names">{{ item.studentNames }}</span>
              </div>
              <div class="annex-row" @click.stop="openAnnexPicker(item)">
                <van-icon name="description" size="12" />
                <span class="annex-text" :class="{ 'annex-uploaded': item.annexCount > 0 }">
                  {{ item.annexCount > 0 ? `教案（${item.annexCount}）` : '上传教案' }}
                </span>
              </div>
            </div>
            <div class="card-right">
              <span
                class="review-btn"
                :class="getReviewStatusClass(item)"
                @click.stop="openReviewPicker(item)"
              >{{ getReviewStatusText(item) }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!hasMore && Object.keys(groupedTeachingLogs).length > 0" class="list-end">
        没有更多了
      </div>
      <van-empty v-if="!hasMore && Object.keys(groupedTeachingLogs).length === 0" description="暂无上课记录" />
    </div>

    <!-- 学员点评选择弹层 -->
    <van-action-sheet
      v-model:show="reviewPickerShow"
      :actions="reviewActions"
      title="选择学员查看点评"
      cancel-text="取消"
      close-on-click-action
      @select="onSelectReviewStudent"
    />

    <!-- 教案管理弹层 -->
    <van-popup v-model:show="annexPickerShow" position="bottom" round :z-index="9999" :style="{ maxHeight: '85%' }">
      <div class="annex-picker">
        <div class="annex-picker-header">
          <span class="annex-picker-title">教案管理</span>
          <van-icon name="cross" size="20" @click="annexPickerShow = false" />
        </div>
        <div class="annex-picker-body">
          <van-uploader
            v-model="annexFileList"
            :max-count="50"
            :max-size="50 * 1024 * 1024"
            accept="*"
            multiple
            :disabled="!annexEditable"
            :deletable="annexEditable"
            :before-read="beforeAnnexRead"
            :after-read="afterAnnexRead"
            @delete="onAnnexDelete"
          />
          <p class="annex-tip">支持图片/视频/PDF/Word等格式，最多50个，单个50MB</p>
        </div>
        <div class="annex-picker-footer">
          <van-button type="primary" block :loading="annexSaving" :disabled="!annexEditable" @click="saveAnnex">
            保存
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onActivated, defineOptions } from 'vue'
import { useRouter } from 'vue-router'
import { showToast, showSuccessToast, showFailToast } from 'vant'
import { getTeacherSchedulePlanListForPage, getTeacherClassHourStatics, getReviewStudentList, updateSchedulePlanAnnex } from '@/api/schedule'
import { uploadFile, listOssByIds } from '@/api/oss'
import StudentPicker from '@/components/StudentPicker.vue'
import { parseDate } from '@/utils/common'

defineOptions({ name: 'MyTeachingLog' })

const router = useRouter()

const pageNum = ref(1)
const pageSize = ref(10)
const groupedTeachingLogs = ref({})
const studentId = ref(null)
const selectedStudent = ref(null)
const hasMore = ref(true)
const classHourStatics = ref({})
const reviewPickerShow = ref(false)
const reviewActions = ref([])
const reviewStudentMap = ref({})

// 教案相关
const annexPickerShow = ref(false)
const annexFileList = ref([])
const annexSaving = ref(false)
const annexEditable = ref(false)
const currentAnnexItem = ref(null)

/** 弹层打开时隐藏底部 tabbar（企微 WebView 中层叠上下文异常，z-index 不生效） */
watch([annexPickerShow, reviewPickerShow], ([annexShow, reviewShow]) => {
  const tabbar = document.querySelector('.van-tabbar')
  if (tabbar) {
    tabbar.style.display = (annexShow || reviewShow) ? 'none' : ''
  }
})

onMounted(() => {
  getSchedulePlanList()
  getClassHourStatics();
})

onActivated(() => {
  if (pageNum.value > 1) {
    pageNum.value = 1
    groupedTeachingLogs.value = {}
    hasMore.value = true
    getSchedulePlanList()
  }
  getClassHourStatics();
})

function getClassHourStatics() {
  getTeacherClassHourStatics({
    studentId: studentId.value
  }).then(response => {
    if (response) {
      classHourStatics.value = response
    }
  })
}

function onStudentChange(student) {
  if (student) {
    studentId.value = student.studentId
  } else {
    studentId.value = null
  }
  onSearch()
}

/** 跳转课时构成详情（累计/本月/本周），带当前学员筛选 */
function goToComposition(timeType) {
  router.push({
    path: '/classHourComposition',
    query: {
      timeType,
      ...(studentId.value ? { studentId: studentId.value } : {})
    }
  })
}

function onSearch() {
  pageNum.value = 1
  groupedTeachingLogs.value = {}
  hasMore.value = true
  getSchedulePlanList()
  getClassHourStatics();
}

function getSchedulePlanList() {
  const params = {
    pageNum: pageNum.value,
    pageSize: pageSize.value,
    studentId: studentId.value
  }
  getTeacherSchedulePlanListForPage(params).then(response => {
    if (response) {
      groupedTeachingLogs.value = {
        ...groupedTeachingLogs.value,
        ...response
      }
    }
    if (!response || Object.keys(response).length < 5) {
      hasMore.value = false
    }
    pageNum.value++
  }).finally(() => {
  })
}

function goToDetail(schedulePlanId) {
  router.push({
    path: '/teachingLogDetail',
    query: { schedulePlanId }
  })
}

/** 打开学员点评选择 */
function openReviewPicker(item) {
  // 只有一个学员时直接跳转点评编辑页
  if (item.studentCount === 1 && item.firstStudentScheduleId) {
    router.push({ path: '/courseEvaluate/' + item.firstStudentScheduleId })
    return
  }
  getReviewStudentList(item.schedulePlanId).then(list => {
    if (!list || list.length === 0) {
      return
    }
    reviewStudentMap.value = {}
    reviewActions.value = list.map(s => {
      reviewStudentMap.value[s.studentName] = s
      return {
        name: s.studentName,
        subname: s.isReview ? '已点评' : '待点评',
        color: s.isReview ? '#07c160' : '#999'
      }
    })
    reviewPickerShow.value = true
  })
}

/** 选择学员跳点评编辑页 */
function onSelectReviewStudent(action) {
  const student = reviewStudentMap.value[action.name]
  if (student) {
    router.push({ path: '/courseEvaluate/' + student.studentScheduleId })
  }
}

/** 打开教案管理弹层 */
function openAnnexPicker(item) {
  currentAnnexItem.value = item
  annexFileList.value = []
  // 课程结束后才可编辑
  const now = new Date()
  const endTime = item.endDateTime ? parseDate(item.endDateTime) : null
  annexEditable.value = !endTime || now > endTime
  annexPickerShow.value = true
  // 回显已上传文件：用 ossId 批量查 URL
  if (item.annex) {
    const ossIds = item.annex.split(',').filter(id => id)
    if (ossIds.length) {
      listOssByIds(ossIds).then(list => {
        if (list && list.length) {
          annexFileList.value = list.map(f => ({
            url: f.url,
            ossId: f.ossId,
            status: 'done'
          }))
        }
      }).catch(() => {
        // 查询失败不影响弹层打开
      })
    }
  }
}

/** 教案上传前校验 */
function beforeAnnexRead(files) {
  const fileListArray = Array.isArray(files) ? files : [files]
  for (const file of fileListArray) {
    if (!file || !file.type) {
      showToast('文件格式异常')
      return false
    }
    if (file.size > 50 * 1024 * 1024) {
      showToast('文件大小不能超过50MB')
      return false
    }
  }
  return true
}

/** 教案上传后处理 */
function afterAnnexRead(file) {
  const files = Array.isArray(file) ? file : [file]
  files.forEach(f => {
    f.status = 'uploading'
    f.message = '上传中...'
    const formData = new FormData()
    formData.append('file', f.file)
    uploadFile(formData).then(res => {
      f.url = res.url
      f.ossId = res.ossId
      f.status = 'done'
      f.message = ''
    }).catch(() => {
      f.status = 'failed'
      f.message = '上传失败'
    })
  })
}

/** 教案删除 */
function onAnnexDelete(file, detail) {
  // van-uploader 自动从列表移除，这里不需要额外处理
}

/** 保存教案 */
function saveAnnex() {
  const uploading = annexFileList.value.some(f => f.status === 'uploading')
  if (uploading) {
    showToast('有文件正在上传，请稍候')
    return
  }
  const failed = annexFileList.value.some(f => f.status === 'failed')
  if (failed) {
    showToast('有文件上传失败，请删除后重试')
    return
  }
  const ossIds = annexFileList.value.map(f => f.ossId).filter(id => id)
  annexSaving.value = true
  updateSchedulePlanAnnex({
    schedulePlanId: currentAnnexItem.value.schedulePlanId,
    annex: ossIds.join(',')
  }).then(() => {
    showSuccessToast('保存成功')
    // 更新当前卡片的教案数量
    if (currentAnnexItem.value) {
      currentAnnexItem.value.annexCount = ossIds.length
      currentAnnexItem.value.annex = ossIds.join(',')
    }
    annexPickerShow.value = false
  }).catch(() => {
    showFailToast('保存失败')
  }).finally(() => {
    annexSaving.value = false
  })
}

/** 点评状态文字 */
function getReviewStatusText(item) {
  const total = item.studentCount || 0
  const reviewed = item.reviewedCount || 0
  if (total === 0) return '点评'
  if (reviewed >= total) return '已点评'
  if (reviewed > 0) return `点评 ${reviewed}/${total}`
  return '点评'
}

/** 点评状态样式 */
function getReviewStatusClass(item) {
  const total = item.studentCount || 0
  const reviewed = item.reviewedCount || 0
  if (total === 0 || reviewed === 0) return 'review-pending'
  if (reviewed >= total) return 'review-done'
  return 'review-doing'
}

function handleScroll(event) {
  const scrollTop = event.target.scrollTop
  const scrollHeight = event.target.scrollHeight
  const clientHeight = event.target.clientHeight
  if (scrollTop + clientHeight >= scrollHeight - 20) {
    getSchedulePlanList()
  }
}
</script>

<style scoped>
.my-teaching-log {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
}

/* 搜索栏 */
.search-bar {
  background-color: #fff;
  padding: 8px 12px;
  flex-shrink: 0;
}

/* 统计卡片 */
.stats-card {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #1677ff 0%, #4096ff 100%);
  margin: 12px 16px;
  border-radius: 12px;
  padding: 16px 0;
  box-shadow: 0 4px 12px rgba(22, 119, 255, 0.2);
  flex-shrink: 0;
}

.stat-item {
  flex: 1;
  text-align: center;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.stat-item:active {
  opacity: 0.7;
}

.stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.stat-divider {
  width: 1px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.2);
}

/* 列表区域 */
.log-list {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 16px 16px;
}

.log-list::-webkit-scrollbar {
  display: none;
}

/* 日期分组 */
.date-section {
  margin-bottom: 16px;
}

.date-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-top: 4px;
}

.date-text {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}

.week-text {
  font-size: 12px;
  color: #999;
}

.count-badge {
  margin-left: auto;
  font-size: 11px;
  color: #1677ff;
  background-color: #e6f4ff;
  padding: 2px 8px;
  border-radius: 10px;
}

/* 课程卡片 */
.card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.course-card {
  display: flex;
  align-items: stretch;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.course-card:active {
  transform: scale(0.98);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.card-left {
  display: flex;
  align-items: center;
  padding: 14px 12px;
  background: linear-gradient(180deg, #f0f7ff 0%, #e6f0ff 100%);
  min-width: 80px;
}

.time-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.time {
  font-size: 13px;
  font-weight: 600;
  color: #1677ff;
}

.time-divider {
  font-size: 10px;
  color: #91caff;
}

.card-content {
  flex: 1;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.course-name {
  font-size: 15px;
  font-weight: 500;
  color: #262626;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #8c8c8c;
}

.student-names {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-right {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 12px;
}

.review-btn {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 10px;
  white-space: nowrap;
}

.review-pending {
  color: #999;
  background-color: #f5f5f5;
}

.review-doing {
  color: #FF7D45;
  background-color: #FFF3E8;
}

.review-done {
  color: #07c160;
  background-color: #e8f8ee;
}

/* 加载和结束提示 */
.list-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  font-size: 13px;
  color: #999;
}

.list-end {
  text-align: center;
  padding: 20px;
  font-size: 12px;
  color: #ccc;
}

/* 教案状态行 */
.annex-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #999;
}

.annex-text {
  color: #999;
}

.annex-uploaded {
  color: #1677ff;
}

/* 教案管理弹层 */
.annex-picker {
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}

.annex-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.annex-picker-title {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.annex-picker-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.annex-tip {
  font-size: 12px;
  color: #999;
  margin: 12px 0 0;
}

.annex-picker-footer {
  padding: 12px 16px 28px;
  padding-bottom: calc(28px + env(safe-area-inset-bottom));
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}
</style>
